import React, { useEffect, useState, useCallback } from 'react';
import { auditAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Loader2,
  ScrollText,
  Search,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Filter,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import Layout from '../components/Layout';

const ACTION_COLORS = {
  event_created: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  event_updated: 'bg-sky-100 text-sky-700 border-sky-200',
  event_deleted: 'bg-rose-100 text-rose-700 border-rose-200',
  event_duplicated: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  registration: 'bg-violet-100 text-violet-700 border-violet-200',
  check_in: 'bg-amber-100 text-amber-700 border-amber-200',
  check_out: 'bg-amber-100 text-amber-700 border-amber-200',
  feedback_submitted: 'bg-teal-100 text-teal-700 border-teal-200',
  certificate_sent: 'bg-blue-100 text-blue-700 border-blue-200',
  certificate_downloaded: 'bg-blue-100 text-blue-700 border-blue-200',
  attendees_exported: 'bg-slate-100 text-slate-700 border-slate-200',
  feedback_exported: 'bg-slate-100 text-slate-700 border-slate-200',
  sponsorship_invitation_created: 'bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200',
  sponsorship_invitation_sent: 'bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200',
  sponsorship_invitation_reminder_sent: 'bg-orange-100 text-orange-700 border-orange-200',
  sponsorship_invitation_downloaded: 'bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200',
};

const prettyAction = (a) => (a || '').replace(/_/g, ' ');

export default function AuditPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [filters, setFilters] = useState({ actions: [], actor_emails: [] });

  // Filter form state
  const [actionFilter, setActionFilter] = useState('all');
  const [actorFilter, setActorFilter] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [searchQ, setSearchQ] = useState('');

  const buildParams = useCallback(() => {
    const p = { page, page_size: pageSize };
    if (actionFilter && actionFilter !== 'all') p.action = actionFilter;
    if (actorFilter && actorFilter !== 'all') p.actor_email = actorFilter;
    if (fromDate) p.from_date = fromDate;
    if (toDate) p.to_date = toDate;
    if (searchQ.trim()) p.q = searchQ.trim();
    return p;
  }, [page, pageSize, actionFilter, actorFilter, fromDate, toDate, searchQ]);

  const fetchPage = useCallback(async () => {
    setLoading(true);
    try {
      const res = await auditAPI.list(buildParams());
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 0);
    } catch (e) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    auditAPI.filters().then((r) => setFilters(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  const resetFilters = () => {
    setActionFilter('all');
    setActorFilter('all');
    setFromDate('');
    setToDate('');
    setSearchQ('');
    setPage(1);
  };

  const hasActiveFilter =
    (actionFilter && actionFilter !== 'all') ||
    (actorFilter && actorFilter !== 'all') ||
    fromDate || toDate || searchQ.trim();

  return (
    <Layout activeNav="audit">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-1 flex items-center gap-2">
              <ScrollText className="w-7 h-7" />
              Audit Log
            </h1>
            <p className="text-muted-foreground">
              Immutable record of all admin actions across the organisation. Useful for RACGP review.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={fetchPage}
            disabled={loading}
            className="gap-2"
            data-testid="audit-refresh-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Refresh
          </Button>
        </div>

        {/* Filter bar */}
        <Card className="border-border mb-6">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Filter className="w-4 h-4" />
              <span>Filters</span>
              {hasActiveFilter && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={resetFilters}
                  className="h-7 px-2 ml-auto"
                  data-testid="audit-clear-filters"
                >
                  <X className="w-3 h-3 mr-1" />
                  Clear
                </Button>
              )}
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Action</Label>
                <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v); setPage(1); }}>
                  <SelectTrigger className="h-10" data-testid="audit-filter-action">
                    <SelectValue placeholder="All actions" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All actions</SelectItem>
                    {filters.actions?.map((a) => (
                      <SelectItem key={a} value={a}>{prettyAction(a)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Actor</Label>
                <Select value={actorFilter} onValueChange={(v) => { setActorFilter(v); setPage(1); }}>
                  <SelectTrigger className="h-10" data-testid="audit-filter-actor">
                    <SelectValue placeholder="All actors" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All actors</SelectItem>
                    {filters.actor_emails?.map((e) => (
                      <SelectItem key={e} value={e}>{e}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">From</Label>
                <Input
                  type="date"
                  value={fromDate}
                  onChange={(e) => { setFromDate(e.target.value); setPage(1); }}
                  className="h-10"
                  data-testid="audit-filter-from"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">To</Label>
                <Input
                  type="date"
                  value={toDate}
                  onChange={(e) => { setToDate(e.target.value); setPage(1); }}
                  className="h-10"
                  data-testid="audit-filter-to"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="details, name, email..."
                    value={searchQ}
                    onChange={(e) => setSearchQ(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); fetchPage(); } }}
                    className="h-10 pl-9"
                    data-testid="audit-filter-search"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="border-border">
          <CardContent className="p-0">
            {loading && items.length === 0 ? (
              <div className="p-12 text-center">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground mx-auto" />
              </div>
            ) : items.length === 0 ? (
              <div className="p-12 text-center text-muted-foreground" data-testid="audit-empty">
                No audit log entries match the current filters.
              </div>
            ) : (
              <div className="overflow-x-auto" data-testid="audit-table">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-muted-foreground">
                    <tr className="text-left">
                      <th className="px-4 py-3 font-medium">Time</th>
                      <th className="px-4 py-3 font-medium">Actor</th>
                      <th className="px-4 py-3 font-medium">Action</th>
                      <th className="px-4 py-3 font-medium">Event</th>
                      <th className="px-4 py-3 font-medium">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it) => (
                      <tr key={it.id} className="border-t border-border align-top" data-testid={`audit-row-${it.id}`}>
                        <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                          {it.timestamp ? format(parseISO(it.timestamp), 'd MMM yyyy HH:mm') : '—'}
                        </td>
                        <td className="px-4 py-3">
                          {it.actor_name || it.actor_email ? (
                            <div>
                              <div className="font-medium text-foreground">{it.actor_name || it.actor_email}</div>
                              {it.actor_name && it.actor_email && (
                                <div className="text-xs text-muted-foreground">{it.actor_email}</div>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground italic">public / system</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <Badge
                            variant="outline"
                            className={`${ACTION_COLORS[it.action] || 'bg-muted text-muted-foreground'} capitalize`}
                          >
                            {prettyAction(it.action)}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          {it.event_title ? (
                            <a
                              href={`/events/${it.event_id}`}
                              className="text-primary hover:underline"
                              data-testid={`audit-event-link-${it.id}`}
                            >
                              {it.event_title}
                            </a>
                          ) : it.event_id ? (
                            <span className="text-xs text-muted-foreground">{it.event_id.slice(0, 8)}…</span>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{it.details || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pagination — always rendered so testid-based checks remain stable */}
        <div className="flex items-center justify-between mt-4 text-sm" data-testid="audit-pagination">
          <p className="text-muted-foreground">
            Page {page} of {Math.max(totalPages, 1)} · {total} entries
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1 || loading}
              data-testid="audit-prev-page"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Prev
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(Math.max(totalPages, 1), p + 1))}
              disabled={page >= totalPages || loading || total === 0}
              data-testid="audit-next-page"
            >
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
